import React, { useEffect, useState } from 'react';
import { Box, Dialog, DialogContent, DialogTitle, Tab, Tabs, useTheme } from '@mui/material';
import { SampleHierarchyGrid } from './SampleHierarchyGrid';
import { DialogCloseButton } from '../../components/DialogCloseButton';
import useMemoTranslation from '../../hooks/UseMemoTranslation';
import { DialogTypography } from '../../components/DialogTypography';
import { SampleHistoryGrid } from './SampleHistoryGrid';
import { LoadingState, LoadState } from '../../components/LoadingStateUtil';
import {
  ConvertToSampleHierarchyTree,
  GetAllLeafSampleIds,
  GetAllSampleIds,
  GetAllTransitionAndTransitionSamples,
  GetHierarchyForSampleIds,
  GetSampleTransitionInformation,
  GetSampleSpecificBranch,
  SampleTransitionInformation,
  SampleHierarchyTree,
} from '../../data/SampleHierarchyData';
import { TransitionSampleDetailsDto } from '../../data/SampleTrackingTransitionData';
import { Dictionary, UseState } from '../../util/TypeUtil';
import { chunk, compact, flatMap, keyBy, map, mapValues, uniqBy } from 'lodash';
import { getSampleIdentifier, Sample } from '../../data/SampleData';
import useAuth from '../../auth/UseAuth';
import { LoadingIndicator } from '../../components/LoadingIndicator';
import { ErrorIndicator } from '../../components/ErrorIndicator';
import { FlexTableBox } from '../../components/FlexTableBox';
import { CustomizableSwitch } from '../../components/CustomizableSwitch';
import { useParams } from 'react-router-dom';
import { SampleInformaticsResultGrid } from './SampleInformaticsResultGrid';
import { SampleLineageDataDialog } from '../../components/SampleLineageDataDialog';
import { SelectableCandidate } from '../../data/SampleTrackingFieldSelectionData';
import { FlexBox } from '../../components/FlexBox';
import { DecryptForeignHashesSwitch } from '../../components/DecryptForeignHashesSwitch';
import { useConfiguredTransitions } from '../../components/hooks/UseConfiguredTransitions';
import { v4 as uuidv4 } from 'uuid';

type PageTabs = 'history' | 'hierarchy' | 'informatics';

export interface SampleHierarchyDialogProps {
  sampleIds: ReadonlyArray<string>;
  completedSampleIds?: ReadonlyArray<string>;
  open: boolean;
  onClose?: () => void;
  isReport?: boolean;
  defaultShowGlobalHistory?: boolean;
}

export const SampleHierarchyDialog = ({
  sampleIds,
  completedSampleIds = [],
  open,
  onClose = () => {},
  isReport = false,
  defaultShowGlobalHistory = false,
}: SampleHierarchyDialogProps) => {
  const { accessToken } = useAuth();

  let { researchProjectId } = useParams() as { researchProjectId: string };
  const { t } = useMemoTranslation();
  const theme = useTheme();

  const [modalOpen, setModalOpen] = useState<boolean>(open);
  const [selectedTab, setSelectedTab] = useState<PageTabs>('history');

  const [loadingState, setLoadingState] = useState<LoadingState>({ status: 'Loading' });

  const [loadedSampleIds, setLoadedSampleIds] = useState<ReadonlyArray<string>>(sampleIds);
  const [selectedSampleIds, setSelectedSampleIds] = useState<ReadonlyArray<string>>(sampleIds);
  const [leafSampleIds, setLeafSampleIds] = useState<ReadonlyArray<string>>([]);
  const [baseHierarchyTreeData, setBaseHierarchyTreeData] = useState<ReadonlyArray<SampleHierarchyTree>>([]);
  const [transitionInformation, setTransitionInformation] = useState<ReadonlyArray<SampleTransitionInformation>>([]);
  const [transitionSample, setTransitionSample] = useState<ReadonlyArray<TransitionSampleDetailsDto>>([]);
  const [samples, setSamples] = useState<ReadonlyArray<Sample>>([]);
  const [identifiersBySampleId, setIdentifiersBySampleId] = useState<Dictionary<string>>({});
  const [identifiersByTransitionSampleId, setIdentifiersByTransitionSampleId] = useState<Dictionary<string>>({});
  const [useDecryptedHashes, setUseDecryptedHashes] = useState<boolean>(false);
  const [filterOutInputOrders, setFilterOutInputOrders] = useState<boolean>(true);
  const [showGlobalHistory, setShowGlobalHistory] = useState<boolean>(defaultShowGlobalHistory);
  const [showExited, setShowExited] = useState<boolean>(false);

  const [selectableCandidates, setSelectableCandidates] = useState<ReadonlyArray<SelectableCandidate>>([]);

  const [branchSpecificHierarchyData, setBranchSpecificHierarchyData] = useState<ReadonlyArray<SampleHierarchyTree>>(
    []
  );
  const [branchSpecificTransitionSampleData, setBranchSpecificTransitionSampleData] = useState<
    ReadonlyArray<TransitionSampleDetailsDto>
  >([]);

  const [configuredTransitions] = useConfiguredTransitions(researchProjectId, setLoadingState);

  useEffect(() => {
    if (!showExited) {
      setSelectedSampleIds(sampleIds);
    } else if (showExited && completedSampleIds.length > 0) {
      const allSampleIds = [...sampleIds, ...completedSampleIds];
      setSelectedSampleIds(allSampleIds);
    }
  }, [showExited, completedSampleIds, sampleIds]);

  useEffect(() => {
    const newSamplesToLoad = selectedSampleIds.filter(id => !loadedSampleIds.includes(id));
    if (newSamplesToLoad.length > 0) {
      setLoadedSampleIds(prev => [...prev, ...newSamplesToLoad]);
    }
  }, [selectedSampleIds, loadedSampleIds]);

  useEffect(() => {
    return LoadState(setLoadingState, async () => {
      if (accessToken && loadedSampleIds && loadedSampleIds.length > 0) {
        const data = flatMap(
          await Promise.all(
            chunk(loadedSampleIds, 1000).map(
              async ids => await GetHierarchyForSampleIds(ids, accessToken, useDecryptedHashes)
            )
          )
        );

        if (data === null) {
          return;
        }

        const uniqueHierarchies = uniqBy(data, h => h.root.sampleId);
        const allSamples = uniqBy(
          uniqueHierarchies.flatMap(d => [
            d.root,
            ...d.hierarchy.map(h => h.ancestor),
            ...d.hierarchy.map(h => h.descendant),
          ]),
          i => i.sampleId
        );
        const allSampleIds = allSamples.map(i => i.sampleId);

        setLoadingState({ status: 'Loading' });
        const informationData = await GetSampleTransitionInformation(allSampleIds, useDecryptedHashes, accessToken);

        setLoadingState({ status: 'Loading' });
        const transitionSamplesData = compact(
          flatMap(
            await Promise.all(
              chunk(allSampleIds, 1000).map(async ids => await GetAllTransitionAndTransitionSamples(ids, accessToken))
            )
          )
        );

        const sampleById = keyBy(allSamples, i => i.sampleId);
        const informationById = keyBy(informationData, i => i.sampleId);

        const identifiersByTransitionSampleId = mapValues(
          keyBy(transitionSamplesData, i => i.transitionSample.transitionSampleId),
          i => {
            const sample = sampleById[i.transitionSample.sampleId];
            return getSampleIdentifier(sample, i.labAssignedSampleLabel);
          }
        );

        const sampleHierarchyMap: Dictionary<string> = {};
        uniqueHierarchies.forEach(hierarchy => {
          hierarchy.hierarchy.forEach(h => {
            sampleHierarchyMap[h.descendant.sampleId] = h.ancestor.sampleId;
          });
        });

        const identifiers = mapValues(
          keyBy(allSampleIds, i => i),
          sampleId => {
            const info = informationById[sampleId];
            const sample = sampleById[sampleId];

            let identifier = getSampleIdentifier(
              sample,
              info?.currentLabAssignedSampleLabel ?? info?.labAssignedSampleLabelCreatedIn
            );

            if (identifier === sampleId && sampleHierarchyMap[sampleId]) {
              const parentId = sampleHierarchyMap[sampleId];
              const parentSample = sampleById[parentId];
              const parentInfo = informationById[parentId];
              const parentIdentifier = getSampleIdentifier(
                parentSample,
                parentInfo?.currentLabAssignedSampleLabel ?? parentInfo?.labAssignedSampleLabelCreatedIn
              );
              if (parentIdentifier && parentIdentifier !== parentId) {
                identifier = parentIdentifier;
              }
            }

            return identifier;
          }
        );

        setSamples(allSamples);
        setIdentifiersBySampleId(identifiers);
        setIdentifiersByTransitionSampleId(identifiersByTransitionSampleId);
        setTransitionInformation(informationData);
        setTransitionSample(transitionSamplesData);
        setBaseHierarchyTreeData(uniqueHierarchies.map(tree => ConvertToSampleHierarchyTree(tree)));
      }
    });
  }, [accessToken, loadedSampleIds, useDecryptedHashes]);

  useEffect(() => {
    const hierarchyData = compact(baseHierarchyTreeData.map(tree => GetSampleSpecificBranch(tree, selectedSampleIds)));
    setBranchSpecificHierarchyData(hierarchyData);

    const allSampleIds = GetAllSampleIds(hierarchyData);
    const allLeafSampleIds = GetAllLeafSampleIds(hierarchyData);

    setLeafSampleIds(allLeafSampleIds);
    setBranchSpecificTransitionSampleData(
      transitionSample.filter(i => allSampleIds.includes(i.transitionSample.sampleId))
    );
  }, [baseHierarchyTreeData, selectedSampleIds, transitionSample]);

  useEffect(() => {
    const informationById = keyBy(transitionInformation, i => i.sampleId);
    const candidates = map(selectedSampleIds, sampleId => {
      const info = informationById[sampleId];
      return { sampleId: info?.sampleId ?? '', entryId: uuidv4() };
    }).filter(i => i.sampleId);

    setSelectableCandidates(candidates);
  }, [selectedSampleIds, transitionInformation]);

  function handleClose() {
    onClose();
    setModalOpen(false);
  }

  return (
    <Dialog maxWidth={'xl'} fullWidth={true} open={modalOpen}>
      <DialogTitle sx={{ minWidth: '300px', paddingBottom: 0 }}>
        <DialogCloseButton onClick={() => handleClose()} />
        {t('sampleHierarchy')}
        {!isReport && (
          <DialogTypography variant='subtitle1' sx={{ maxWidth: '75%' }}>
            {t('sampleHierarchyInfo')}
          </DialogTypography>
        )}
      </DialogTitle>
      <DialogContent>
        <Box mb={1}>
          <Tabs
            value={selectedTab}
            onChange={(event: React.SyntheticEvent, newValue: PageTabs) => {
              setSelectedTab(newValue);
            }}
            sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 0 }}
          >
            <Tab value='history' label={t('history')} sx={{ color: theme.colors.alpha.black[100] }} />
            <Tab value='hierarchy' label={t('hierarchy')} sx={{ color: theme.colors.alpha.black[100] }} />
            <Tab value='informatics' label={t('informatics')} sx={{ color: theme.colors.alpha.black[100] }} />
          </Tabs>
        </Box>
        <FlexTableBox sx={{ height: '60vh' }}>
          {selectedTab === 'history' && (
            <SampleHistoryGrid
              identifiersByTransitionSampleId={identifiersByTransitionSampleId}
              transitionInformation={transitionInformation}
              transitionSample={branchSpecificTransitionSampleData}
              selectedSampleIds={selectedSampleIds}
              setSelectedSampleIds={setSelectedSampleIds}
              filterOutInputOrders={filterOutInputOrders}
              showGlobalHistory={showGlobalHistory}
              samples={samples}
              configuredTransitions={configuredTransitions}
              loadingProps={[loadingState, setLoadingState]}
              leftActions={
                <LeftActions
                  selectedTab={selectedTab}
                  filterOutInputOrderProps={[filterOutInputOrders, setFilterOutInputOrders]}
                  showGlobalHistoryProps={[showGlobalHistory, setShowGlobalHistory]}
                  showExitedProps={[showExited, setShowExited]}
                  decryptedHashesProps={[useDecryptedHashes, setUseDecryptedHashes]}
                  isReport={isReport}
                />
              }
              rightActions={<RightActions selectableCandidates={selectableCandidates} />}
              leafSampleIds={leafSampleIds}
              completedSampleIds={completedSampleIds}
            />
          )}
          {selectedTab === 'hierarchy' && (
            <SampleHierarchyGrid
              identifiersBySampleId={identifiersBySampleId}
              transitionInformation={transitionInformation}
              hierarchyTreeData={branchSpecificHierarchyData}
              selectedSampleIds={selectedSampleIds}
              setSelectedSampleIds={setSelectedSampleIds}
              configuredTransitions={configuredTransitions}
              loadingProps={[loadingState, setLoadingState]}
              leftActions={
                <LeftActions
                  selectedTab={selectedTab}
                  filterOutInputOrderProps={[filterOutInputOrders, setFilterOutInputOrders]}
                  showGlobalHistoryProps={[showGlobalHistory, setShowGlobalHistory]}
                  showExitedProps={[showExited, setShowExited]}
                  decryptedHashesProps={[useDecryptedHashes, setUseDecryptedHashes]}
                  isReport={isReport}
                />
              }
              rightActions={<RightActions selectableCandidates={selectableCandidates} />}
              completedSampleIds={completedSampleIds}
            />
          )}
          {selectedTab === 'informatics' && (
            <SampleInformaticsResultGrid
              identifiersBySampleId={identifiersBySampleId}
              transitionInformation={transitionInformation}
              selectedSampleIds={selectedSampleIds}
              loadingProps={[loadingState, setLoadingState]}
              leftActions={
                <LeftActions
                  selectedTab={selectedTab}
                  filterOutInputOrderProps={[filterOutInputOrders, setFilterOutInputOrders]}
                  showGlobalHistoryProps={[showGlobalHistory, setShowGlobalHistory]}
                  showExitedProps={[showExited, setShowExited]}
                  decryptedHashesProps={[useDecryptedHashes, setUseDecryptedHashes]}
                  isReport={isReport}
                />
              }
              rightActions={<RightActions selectableCandidates={selectableCandidates} />}
            />
          )}
        </FlexTableBox>
      </DialogContent>
      <LoadingIndicator loadingState={loadingState} margin={'LRB'} />
      <ErrorIndicator loadingState={loadingState} />
    </Dialog>
  );
};

const LeftActions = ({
  selectedTab,
  filterOutInputOrderProps,
  showGlobalHistoryProps,
  showExitedProps,
  decryptedHashesProps,
  isReport,
}: {
  selectedTab: PageTabs;
  filterOutInputOrderProps: UseState<boolean>;
  showGlobalHistoryProps: UseState<boolean>;
  showExitedProps: UseState<boolean>;
  decryptedHashesProps: UseState<boolean>;
  isReport: boolean;
}) => {
  const { t } = useMemoTranslation();

  const [filterOutInputOrders, setFilterOutInputOrders] = filterOutInputOrderProps;
  const [showGlobalHistory, setShowGlobalHistory] = showGlobalHistoryProps;
  const [showExited, setShowExited] = showExitedProps;

  return (
    <FlexBox>
      <DecryptForeignHashesSwitch decryptedHashesProps={decryptedHashesProps} />
      {selectedTab === 'history' && (
        <>
          <CustomizableSwitch
            title={t('filterOutInputOrders')}
            checked={filterOutInputOrders}
            onChange={() => {
              setFilterOutInputOrders(!filterOutInputOrders);
            }}
          />
          <CustomizableSwitch
            title={t('showGlobalHistory')}
            checked={showGlobalHistory}
            onChange={() => {
              setShowGlobalHistory(!showGlobalHistory);
            }}
          />
          {isReport && (
            <CustomizableSwitch
              title={t('showExited')}
              checked={showExited}
              onChange={() => setShowExited(!showExited)}
            />
          )}
        </>
      )}
    </FlexBox>
  );
};

const RightActions = ({ selectableCandidates }: { selectableCandidates: ReadonlyArray<SelectableCandidate> }) => {
  return <SampleLineageDataDialog candidates={selectableCandidates} />;
};
